import { Component } from '@theme/component';

/**
 * Announcement banner custom element that allows fading between content.
 * Based on the Slideshow component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} slideshowContainer
 * @property {HTMLElement[]} [slides]
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 *
 * @extends {Component<Refs>}
 */
export class AnnouncementBar extends Component {
  #current = 0;

  /**
   * The interval ID for automatic playback.
   * @type {number|undefined}
   */
  #interval = undefined;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('mouseenter', this.suspend);
    this.addEventListener('mouseleave', this.resume);
    this.addEventListener('focusin', this.suspend);
    this.addEventListener('focusout', this.#handleFocusOut);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);

    // @app blocks render as bare children — wrap them so the ref system picks them up.
    this.#wrapAppBlocks();
    super.updatedCallback(); // force ref re-scan after DOM mutation
    this.current = 0;        // apply correct aria-hidden to all slides

    this.play();
  }

  /**
   * Called by the Section Rendering API when the section is re-rendered (e.g. theme editor).
   */
  updatedCallback() {
    this.#wrapAppBlocks();
    super.updatedCallback();
    this.current = this.#current;
  }

  /**
   * Wraps direct children of `.announcement-bar__slides` that are not already
   * `<slideshow-slide>` elements (i.e. @app block output) in a slideshow-slide
   * so the ref system and autoplay treat them as normal slides.
   */
  #wrapAppBlocks() {
    const container = this.querySelector('.announcement-bar__slides');
    if (!container) return;

    for (const child of [...container.children]) {
      if (child.tagName.toLowerCase() === 'slideshow-slide') continue;

      const slide = document.createElement('slideshow-slide');
      slide.setAttribute('ref', 'slides[]');
      slide.classList.add('announcement-bar__slide');
      slide.setAttribute('aria-hidden', 'true');
      child.replaceWith(slide);
      slide.appendChild(child);
    }
  }

  /**
   * @param {Event} [event] - Present when a user pressed an arrow; absent for autoplay.
   */
  next(event) {
    if (event) this.#setLive(true);
    this.current += 1;
  }

  /**
   * @param {Event} [event] - Present when a user pressed an arrow.
   */
  previous(event) {
    if (event) this.#setLive(true);
    this.current -= 1;
  }

  /**
   * WAI-ARIA APG carousel: the slides are a live region only while they do not rotate on their own.
   * @param {boolean} polite
   */
  #setLive(polite) {
    if (!this.autoplay) return;

    this.querySelector('.announcement-bar__slides')?.setAttribute('aria-live', polite ? 'polite' : 'off');
  }

  /**
   * Starts automatic slide playback.
   * @param {number} [interval] - The time interval in seconds between slides.
   */
  play(interval = this.autoplayInterval) {
    if (!this.autoplay) return;

    this.paused = false;
    this.#setLive(false);

    this.#interval = setInterval(() => {
      if (this.matches(':hover') || this.#hasKeyboardFocus() || this.#hasOpenDialog() || document.hidden) return;

      // An arrow press may have left the region polite while the interval kept running.
      this.#setLive(false);
      this.next();
    }, interval);
  }

  /**
   * Pauses automatic slide playback.
   */
  pause() {
    this.paused = true;
    this.suspend();
  }

  get paused() {
    return this.hasAttribute('paused');
  }

  set paused(paused) {
    this.toggleAttribute('paused', paused);
  }

  /**
   * Suspends automatic slide playback.
   */
  suspend() {
    clearInterval(this.#interval);
    this.#interval = undefined;
    this.#setLive(true);
  }

  /**
   * Resumes automatic slide playback if autoplay is enabled.
   */
  resume() {
    // Keyboard focus inside keeps rotation stopped and the region polite. A button merely focused by a
    // mouse click (Chromium, Firefox) does not, or rotation would never restart once the pointer leaves.
    // An open zone modal is left to the tick, so rotation restarts by itself when the modal closes.
    if (!this.autoplay || this.paused || this.#hasKeyboardFocus()) return;

    this.suspend();
    this.play();
  }

  /**
   * @returns {boolean} Whether focus inside the bar came from the keyboard (or a focus() call on a text field).
   */
  #hasKeyboardFocus() {
    try {
      return Boolean(this.querySelector(':focus-visible'));
    } catch {
      return this.matches(':focus-within');
    }
  }

  /**
   * The zone modal of the Growing Zones app block renders inside a slide and stays in the DOM, display:none when closed.
   * @returns {boolean}
   */
  #hasOpenDialog() {
    return [...this.querySelectorAll('[aria-modal="true"], dialog[open]')].some(
      (dialog) => dialog.getClientRects().length > 0
    );
  }

  get autoplay() {
    return Boolean(this.autoplayInterval);
  }

  get autoplayInterval() {
    const interval = this.getAttribute('autoplay');
    const value = parseInt(`${interval}`, 10);

    if (Number.isNaN(value)) return undefined;

    return value * 1000;
  }

  get current() {
    return this.#current;
  }

  set current(current) {
    this.#current = current;

    let relativeIndex = current % (this.refs.slides ?? []).length;
    if (relativeIndex < 0) {
      relativeIndex += (this.refs.slides ?? []).length;
    }

    this.refs.slides?.forEach((slide, index) => {
      slide.setAttribute('aria-hidden', `${index !== relativeIndex}`);
      slide.inert = index !== relativeIndex;
    });
  }

  /**
   * Suspend the slideshow when the page is hidden; resume when it becomes visible.
   * Uses suspend() rather than pause() so the paused flag stays clear and resume()
   * can restart the interval when the tab comes back into view.
   */
  #handleVisibilityChange = () => (document.hidden ? this.suspend() : this.resume());

  /**
   * Resume rotation only once focus has left the bar, not when it moves between its controls.
   * @param {FocusEvent} event
   */
  #handleFocusOut = (event) => {
    if (event.relatedTarget instanceof Node && this.contains(event.relatedTarget)) return;

    this.resume();
  };
}

if (!customElements.get('announcement-bar-component')) {
  customElements.define('announcement-bar-component', AnnouncementBar);
}
